import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Calendar,
  Store,
  Truck,
  RefreshCw,
  Bell,
  BellRing,
  Loader2
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { usePushNotifications } from '@/context/PushNotificationContext';
import { toast } from 'sonner';
import { OrderDetailsModal } from '@/components/OrderDetailsModal';
import { API } from '@/lib/api';

const FULFILLMENT_CONFIG = {
  PICKUP: {
    label: 'Ritiro in sede',
    color: 'bg-success',
    icon: Store,
    calendarColor: '#22c55e'
  },
  DELIVERY: {
    label: 'Consegna',
    color: 'bg-instagram-purple',
    icon: Truck,
    calendarColor: '#833AB4'
  }
};

const toIsoStart = (order) => {
  if (order?.fulfillment_type === 'PICKUP' && order?.pickup_datetime) {
    return order.pickup_datetime;
  }

  if (order?.fulfillment_type === 'PICKUP' && order?.pickup_date && order?.pickup_time) {
    return `${order.pickup_date}T${order.pickup_time}:00`;
  }

  return order?.created_at || new Date().toISOString();
};

export const AdminDashboard = () => {
  const auth = useAuth();
  const user = auth?.user || auth?.currentUser || null;
  const token = auth?.token || user?.token || null;

  const authHeaders = useCallback(
    (extra = {}) => ({
      ...extra,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }),
    [token]
  );

  const { addPushNotification } = usePushNotifications();

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const audioRef = useRef(null);
  const processedOrdersRef = useRef(new Set());
  const calendarRef = useRef(null);

  const showNewOrderNotification = useCallback(
    (order) => {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
      }

      addPushNotification({
        id: order?.id,
        title: 'Nuovo ordine ricevuto',
        customer_name: order?.customer_name,
        customer_phone: order?.customer_phone,
        total: Number(order?.total || 0),
        delivery_type: order?.fulfillment_type,
        created_at: order?.created_at || new Date().toISOString()
      });
    },
    [addPushNotification]
  );


const fetchOrders = useCallback(async () => {
  if (!token) {
    setIsLoading(false);
    return;
  }

  try {
    const response = await fetch(
      `${window.location.origin}/menu-digitale/api/admin/orders.php`,
      {
        method: 'GET',
        headers: authHeaders(),
        credentials: 'include',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(`Errore caricamento ordini (${response.status})`);
    }

    const data = await response.json();
    const arr = Array.isArray(data) ? data : [];

    setOrders(arr);

    if (notificationsEnabled) {
      arr.forEach((order) => {
        const id = order?.id;
        if (!id) return;

        if (order?.status === 'paid' && !processedOrdersRef.current.has(id)) {
          showNewOrderNotification(order);
          processedOrdersRef.current.add(id);
        }
      });
    }
  } catch (error) {
    console.error('Error fetching orders:', error);
    toast.error('Errore nel caricamento degli ordini');
  } finally {
    setIsLoading(false);
  }
}, [token, authHeaders, notificationsEnabled, showNewOrderNotification]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleOrderCancelSuccess = (orderId, reason) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? { ...o, status: 'cancelled', cancelled_reason: reason }
          : o
      )
    );
    setIsDetailOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderDeleteSuccess = (orderId) => {
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    setIsDetailOpen(false);
    setSelectedOrder(null);
  };

  const calendarEvents = useMemo(() => {
    return (orders || []).map((order) => {
      const fulfillment = FULFILLMENT_CONFIG[order?.fulfillment_type];
      const start = toIsoStart(order);

      let backgroundColor = fulfillment?.calendarColor || '#833AB4';
      if (order?.status === 'cancelled') {
        backgroundColor = '#ef4444';
      }

      const totalNum = Number(order?.total || 0);
      const customer = order?.customer_name || 'Cliente';

      return {
        id: String(order?.id ?? ''),
        title: `${customer} - €${totalNum.toFixed(2)}`,
        start,
        backgroundColor,
        borderColor: backgroundColor,
        extendedProps: { order }
      };
    });
  }, [orders]);

  const handleEventClick = (info) => {
    const order = info?.event?.extendedProps?.order || null;
    setSelectedOrder(order);
    setIsDetailOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <audio ref={audioRef} preload="auto">
        <source
          src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
          type="audio/mpeg"
        />
      </audio>

      <header className="bg-card border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full gradient-instagram flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Calendario Ordini</h1>
              <p className="text-xs text-muted-foreground">Gestione ordini Sapori 1989</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant={notificationsEnabled ? 'default' : 'outline'}
              size="sm"
              onClick={() => setNotificationsEnabled((v) => !v)}
              className={notificationsEnabled ? 'gradient-instagram text-primary-foreground' : ''}
            >
              {notificationsEnabled ? (
                <BellRing className="w-4 h-4 mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              {notificationsEnabled ? 'Notifiche ON' : 'Notifiche OFF'}
            </Button>

            <Button variant="outline" size="sm" onClick={fetchOrders} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="text-sm text-muted-foreground">Legenda:</span>

          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-success" />
            <span className="text-sm">Ritiro in sede</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-instagram-purple" />
            <span className="text-sm">Consegna</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-destructive" />
            <span className="text-sm">Annullato</span>
          </div>
        </div>

        <Card className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-instagram-purple" />
            </div>
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              locale="it"
              timeZone="Europe/Rome"
              events={calendarEvents}
              eventClick={handleEventClick}
              height="auto"
              buttonText={{
                today: 'Oggi',
                month: 'Mese',
                week: 'Settimana',
                day: 'Giorno'
              }}
              dayMaxEvents={3}
              moreLinkText={(n) => `+${n} altri`}
            />
          )}
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Card className="p-4">
            <div className="text-2xl font-bold text-foreground">
              {(orders || []).filter((o) => o?.status === 'paid').length}
            </div>
            <div className="text-sm text-muted-foreground">Ordini da preparare</div>
          </Card>

          <Card className="p-4">
            <div className="text-2xl font-bold text-success">
              {(orders || []).filter((o) => o?.fulfillment_type === 'PICKUP').length}
            </div>
            <div className="text-sm text-muted-foreground">Ritiri totali</div>
          </Card>

          <Card className="p-4">
            <div className="text-2xl font-bold text-instagram-purple">
              {(orders || []).filter((o) => o?.fulfillment_type === 'DELIVERY').length}
            </div>
            <div className="text-sm text-muted-foreground">Consegne totali</div>
          </Card>

          <Card className="p-4">
            <div className="text-2xl font-bold text-destructive">
              {(orders || []).filter((o) => o?.status === 'cancelled').length}
            </div>
            <div className="text-sm text-muted-foreground">Annullati</div>
          </Card>
        </div>
      </main>

      <OrderDetailsModal
        orderId={selectedOrder?.id}
        orderData={selectedOrder}
        open={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedOrder(null);
        }}
        onCancelSuccess={handleOrderCancelSuccess}
        onDeleteSuccess={handleOrderDeleteSuccess}
        token={token}
      />
    </div>
  );
};
